import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, DollarSign } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const input = "bg-pb-elevated border border-pb-border px-3 py-2 text-white focus:outline-none focus:border-pb-red text-sm w-full";

export default function AffiliateControl() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/affiliates");
      setData(data);
      setSettings((s) => s || data.settings);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/admin/affiliates/settings", {
        cash_rate_pct: Number(settings.cash_rate_pct),
        credit_per_referral: Number(settings.credit_per_referral),
        default_reward: settings.default_reward,
      });
      toast.success("Affiliate program settings saved");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const updateAffiliate = async (a, body, msg) => {
    try {
      await api.put(`/admin/affiliates/${a.id}`, body);
      toast.success(msg);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const markPaid = async (a) => {
    if (!window.confirm(`Mark all pending commissions for ${a.name} ($${a.pending_cash.toFixed(2)}) as PAID? Do this after you've actually sent the payout.`)) return;
    try {
      const { data } = await api.post(`/admin/affiliates/${a.id}/mark-paid`);
      toast.success(`${data.marked_paid} commission(s) marked paid`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data || !settings) return <div className="p-4 text-sm text-zinc-500 animate-pulse">Loading affiliate program…</div>;

  return (
    <div className="mt-4 space-y-6" data-testid="affiliate-control">
      {/* Global settings */}
      <div className="bg-pb-surface border border-pb-border p-5">
        <div className="flex items-center gap-2 mb-1">
          <DollarSign className="w-4 h-4 text-pb-red" />
          <span className="font-display font-bold text-lg uppercase">Program Settings</span>
        </div>
        <p className="text-xs text-zinc-500 mb-4">Defaults for every affiliate. You can override reward type and rate per affiliate below.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Cash Commission (%)</label>
            <input data-testid="affiliate-setting-rate" type="number" min={0} max={100} step={0.5} value={settings.cash_rate_pct}
              onChange={(e) => setSettings({ ...settings, cash_rate_pct: e.target.value })} className={input} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Store Credit / Referral ($)</label>
            <input data-testid="affiliate-setting-credit" type="number" min={0} step={1} value={settings.credit_per_referral}
              onChange={(e) => setSettings({ ...settings, credit_per_referral: e.target.value })} className={input} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Default Reward</label>
            <select data-testid="affiliate-setting-reward" value={settings.default_reward}
              onChange={(e) => setSettings({ ...settings, default_reward: e.target.value })} className={input}>
              <option value="cash">Cash commission</option>
              <option value="credit">Store credit</option>
              <option value="both">Both</option>
            </select>
          </div>
        </div>
        <button data-testid="affiliate-settings-save" onClick={saveSettings} disabled={saving}
          className="mt-4 bg-pb-red text-black font-bold uppercase tracking-widest px-5 py-2.5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      {/* Affiliates list / leaderboard */}
      <div className="bg-pb-surface border border-pb-border">
        <div className="flex items-center gap-2 p-5 border-b border-pb-border">
          <Trophy className="w-4 h-4 text-pb-red" />
          <span className="font-display font-bold text-lg uppercase">Affiliates ({data.affiliates.length})</span>
          <span className="text-xs text-zinc-500 ml-2">ranked by total earnings</span>
        </div>
        {data.affiliates.length === 0 ? (
          <div className="p-5 text-sm text-zinc-600">No affiliates yet. Members can join from their Profile page.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-pb-border/60">
                  <th className="text-left px-4 py-3">#</th>
                  <th className="text-left px-4 py-3">Affiliate</th>
                  <th className="text-left px-4 py-3">Code</th>
                  <th className="text-right px-4 py-3">Refs</th>
                  <th className="text-right px-4 py-3">Conv.</th>
                  <th className="text-right px-4 py-3">Pending</th>
                  <th className="text-right px-4 py-3">Paid</th>
                  <th className="text-right px-4 py-3">Credit</th>
                  <th className="text-left px-4 py-3">Reward</th>
                  <th className="text-left px-4 py-3">Rate %</th>
                  <th className="text-center px-4 py-3">Active</th>
                  <th className="text-left px-4 py-3">Payout</th>
                </tr>
              </thead>
              <tbody>
                {data.affiliates.map((a, i) => (
                  <tr key={a.id} data-testid={`affiliate-row-${a.code}`} className="border-b border-pb-border/40 hover:bg-pb-elevated/50">
                    <td className="px-4 py-3 font-display font-black text-pb-red">{i + 1}</td>
                    <td className="px-4 py-3 font-semibold whitespace-nowrap">{a.name}</td>
                    <td className="px-4 py-3 font-mono text-pb-cyan text-xs">{a.code}</td>
                    <td className="px-4 py-3 text-right font-mono">{a.signups}</td>
                    <td className="px-4 py-3 text-right font-mono">{a.conversions}</td>
                    <td className="px-4 py-3 text-right font-mono text-amber-400">${a.pending_cash.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-pb-red">${a.paid_cash.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-pb-cyan">${a.credit_earned.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <select data-testid={`affiliate-reward-${a.code}`} value={a.reward_type || "inherit"}
                        onChange={(e) => updateAffiliate(a, { reward_type: e.target.value }, "Reward type updated")}
                        className="bg-pb-elevated border border-pb-border text-xs px-2 py-1.5 focus:outline-none focus:border-pb-red">
                        <option value="inherit">Default</option>
                        <option value="cash">Cash</option>
                        <option value="credit">Credit</option>
                        <option value="both">Both</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <input data-testid={`affiliate-rate-${a.code}`} type="number" min={0} max={100} step={0.5}
                        defaultValue={a.cash_rate_pct ?? ""} placeholder="def"
                        onBlur={(e) => {
                          const v = e.target.value;
                          if (v === "" && a.cash_rate_pct != null) updateAffiliate(a, { clear_rate: true }, "Rate reset to default");
                          else if (v !== "" && Number(v) !== a.cash_rate_pct) updateAffiliate(a, { cash_rate_pct: Number(v) }, "Rate override saved");
                        }}
                        className="w-16 bg-pb-elevated border border-pb-border text-xs px-2 py-1.5 focus:outline-none focus:border-pb-red" />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Switch data-testid={`affiliate-active-${a.code}`} checked={a.active}
                        onCheckedChange={(v) => updateAffiliate(a, { active: v }, v ? `${a.name} activated` : `${a.name} suspended`)}
                        className="data-[state=checked]:bg-pb-red" />
                    </td>
                    <td className="px-4 py-3">
                      <button data-testid={`affiliate-mark-paid-${a.code}`} onClick={() => markPaid(a)} disabled={a.pending_cash <= 0}
                        className="text-xs font-bold uppercase tracking-widest border border-pb-border hover:border-pb-red hover:text-pb-red px-3 py-1.5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                        Mark Paid
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
